import { useMemo, useState } from 'react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import PageHeader from '@/components/feature/PageHeader';
import Chip from '@/components/base/Chip';
import EmptyState from '@/components/base/EmptyState';
import ContentListItem from '@/components/base/ContentListItem';
import { getByCategory, getTags, sortByHot, sortByLatest, type ContentItem } from '@/utils/content';

const PAGE_SIZE = 6;

export default function Lifestyle() {
  useDocumentTitle(
    '小 - 生活服务频道 - 多元内容聚合平台',
    '健康、美食、旅游、购物等生活服务资讯，实用的生活指南让日常更从容。',
    '小,生活服务,健康,美食,旅游,购物'
  );

  const all = useMemo(() => getByCategory('lifestyle'), []);
  const tags = useMemo(() => getTags('lifestyle'), []);
  const [tag, setTag] = useState('全部');
  const [sort, setSort] = useState<'latest' | 'hot'>('latest');
  const [visible, setVisible] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const list = all.filter((item) => tag === '全部' || item.tag === tag);
    return sort === 'latest' ? sortByLatest(list) : sortByHot(list);
  }, [all, tag, sort]);

  const visibleList: ContentItem[] = filtered.slice(0, visible);

  const reset = () => {
    setTag('全部');
    setSort('latest');
    setVisible(PAGE_SIZE);
  };

  const changeTag = (value: string) => {
    setTag(value);
    setVisible(PAGE_SIZE);
  };

  return (
    <>
      <PageHeader
        title="生活服务资讯与实用指南"
        description="从健康养生到美食菜谱，从周末旅行到购物攻略，覆盖日常生活的方方面面。"
        crumbs={[{ label: '首页', to: '/' }, { label: '生活' }]}
        icon="ri-store-2-line"
      />

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <div className="mb-6 flex flex-col gap-4 border-b border-background-200/70 pb-5 md:flex-row md:items-center md:justify-between">
          <div className="portal-scroll flex items-center gap-2 overflow-x-auto pb-1 md:pb-0">
            <Chip label="全部" active={tag === '全部'} onClick={() => changeTag('全部')} />
            {tags.map((t) => (
              <Chip key={t} label={t} active={tag === t} onClick={() => changeTag(t)} />
            ))}
          </div>
          <div className="flex flex-shrink-0 items-center gap-1 rounded-full bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setSort('latest')}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                sort === 'latest' ? 'bg-background-50 text-primary-600' : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className="ri-time-line mr-1"></i>最新
            </button>
            <button
              type="button"
              onClick={() => setSort('hot')}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                sort === 'hot' ? 'bg-background-50 text-primary-600' : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className="ri-fire-line mr-1"></i>最热
            </button>
          </div>
        </div>

        <p className="mb-5 text-sm text-foreground-600">
          共找到 <strong className="text-foreground-950">{filtered.length}</strong> 条生活服务内容
        </p>

        {visibleList.length === 0 ? (
          <EmptyState
            title="该标签下暂时没有内容"
            description="试试切换其他生活标签，或查看全部生活服务内容。"
            onReset={reset}
            resetLabel="查看全部"
          />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-x-8 lg:grid-cols-2">
              {visibleList.map((item) => (
                <ContentListItem key={item.id} item={item} />
              ))}
            </div>
            {visible < filtered.length && (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-7 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-add-line"></i>
                  加载更多（剩余 {filtered.length - visible} 条）
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}